import React, { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { Zone } from '@/core/api/entities/zone/zone'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useLocations } from '@/core/hooks/useLocation'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'

interface EditZoneModalProps {
	visible: boolean
	zone: Zone | null
	onClose: () => void
	onSubmit: (values: UpdateZoneRequestDTO) => void
	isLoading: boolean
}

const EditZoneModal: React.FC<EditZoneModalProps> = ({
	visible,
	zone,
	onClose,
	onSubmit,
	isLoading,
}) => {
	const { t } = useTranslation('common')

	// Fetch locations
	const { data: locationsData, isLoading: isLoadingLocations } = useLocations({
		config: useAccountConfig({
			params: {
				per_page: 100, // Get all locations
				include: 'players',
			},
		}),
		options: {
			enabled: visible,
		},
	})

	const locations = locationsData?.data || []
	const defaultStoreId = zone?.location?.id || undefined

	// Create validation schema using Yup
	const validationSchema = Yup.object().shape({
		name: Yup.string()
			.required(t('validation.required'))
			.max(60, t('validation.maxLength', { max: 60 })),
		store_id: Yup.number().required(t('validation.required')),
	})

	const {
		control,
		handleSubmit,
		formState: { errors: _errors },
		reset,
	} = useForm<UpdateZoneRequestDTO>({
		defaultValues: {
			name: zone?.name || '',
			store_id: defaultStoreId,
		},
		resolver: yupResolver(validationSchema) as any,
	})

	// Reset form when zone changes
	useEffect(() => {
		if (zone) {
			reset({
				name: zone.name,
				store_id: defaultStoreId,
			})
		}
	}, [zone, reset])

	const handleFormSubmit = (data: UpdateZoneRequestDTO) => {
		onSubmit(data)
	}

	const handleClose = () => {
		if (!isLoading) {
			onClose()
		}
	}

	return (
		<Dialog open={visible} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-[560px] p-0 gap-0 overflow-hidden flex flex-col"
				showCloseButton={false}
			>
				{/* Header */}
				<div className="px-6 py-5 border-b">
					<DialogTitle className="text-lg font-medium">
						{t('entities.zone.edit.title')}
					</DialogTitle>
				</div>

				{/* Form Content */}
				<form
					onSubmit={handleSubmit(handleFormSubmit)}
					className="flex flex-col flex-1 min-h-0"
				>
					<div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
						{/* Location Select */}
						<div>
							<Label className="mb-2 block text-xs font-medium">
								{t('entities.zone.edit.location_label')}
							</Label>
							<Controller
								name="store_id"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<div>
										<Select
											value={field.value?.toString()}
											onValueChange={(value) => field.onChange(Number(value))}
											disabled={isLoading || isLoadingLocations}
										>
											<SelectTrigger className="h-[38px] w-full text-sm">
												<SelectValue
													placeholder={t(
														'entities.zone.edit.location_placeholder'
													)}
												/>
											</SelectTrigger>
											<SelectContent>
												{locations.map((location) => (
													<SelectItem
														key={location.id}
														value={location.id.toString()}
													>
														{location.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{error && <ErrorMessage error={error.message ?? ''} />}
									</div>
								)}
							/>
						</div>

						{/* Zone Name Input */}
						<div>
							<Label className="mb-2 block text-xs font-medium">
								{t('entities.zone.edit.name_label')}
							</Label>
							<Controller
								name="name"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<div>
										<Input
											{...field}
											placeholder={t('entities.zone.edit.name_placeholder')}
											disabled={isLoading}
											className="h-[38px] text-sm"
										/>
										{error && <ErrorMessage error={error.message ?? ''} />}
									</div>
								)}
							/>
						</div>
					</div>

					{/* Footer */}
					<div className="px-6 py-5 border-t flex justify-end gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={handleClose}
							disabled={isLoading}
							className="rounded-full px-4 h-8 text-xs bg-secondary border-border hover:bg-muted"
						>
							{t('actions.cancel')}
						</Button>
						<Button
							type="submit"
							disabled={isLoading}
							className="rounded-full px-4 h-8 text-xs bg-white text-black hover:bg-gray-100"
						>
							{isLoading ? t('actions.loading') : t('actions.save')}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	)
}

export default EditZoneModal
